import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, EditOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/images/cropImageUtils';
import { useCardContext } from 'contexts/CardContext';
import { useParams } from 'react-router-dom';
import { getCardById } from 'apis/cardApi';
import { validateUUID } from 'utils/validator';


const CompanyLogo = ({ 
  onCroppedImage, 
  onDeleteCompanylogo
}) => {
  const [image, setImage] = useState(null);
  const [open, setOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const { cardState, setCardCompanyLogoUrl } = useCardContext();
  const [cardDetails, setCardDetails] = useState(null);
  const { cardId } = useParams();
  const [loading, setLoading] = useState(true);

  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  const cardCompanyLogoUrl = cardState[cardId]?.cardCompanyLogoUrl;
  const [inputDisabled, setInputDisabled] = useState(false);

  const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
    const containerWidth = window.innerWidth * 0.9;
    const containerHeight = window.innerHeight * 0.9;
    const widthRatio = containerWidth / mediaWidth;
    const heightRatio = containerHeight / mediaHeight;
    return Math.min(widthRatio, heightRatio);
  }, []);

  const onMediaLoaded = useCallback((mediaSize) => {
    const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
    setZoom(initialZoom);
    setCrop({ x: 0, y: 0 });
  }, [calculateInitialZoom]);





  useEffect(() => {
    if (cardDetails?.card && !validateUUID(cardId)) {
      setCardCompanyLogoUrl(cardDetails.card.companyLogoUrl || '');
    }
  }, [cardDetails]);




  useEffect(() => {
    if (!validateUUID(cardId)) {
      setCardCompanyLogoUrl(cardId, null);

      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }
  }, [cardId, objectUrl]);


  useEffect(() => {
    const fetchCardDetails = async () => {
      try {
        const data = await getCardById(cardId);
        setCardDetails(data);
      } catch (error) {
      }
    };

    if (cardId && !validateUUID(cardId)) {
      fetchCardDetails();
    }
  }, [cardId]);




  useEffect(() => {
    if (cardDetails && !validateUUID(cardId)) {
      setCroppedImage(cardDetails.card.companyLogoUrl);


    }
  }, [cardDetails]);

  const handleImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = URL.createObjectURL(event.target.files[0]);
      setObjectUrl(file);
      setImage(file);

      const img = new Image();
      img.onload = () => {
        const initialZoom = calculateInitialZoom(img.width, img.height);
        setZoom(initialZoom);
      };
      img.src = file;

      setOpen(true);
      event.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (onDeleteCompanylogo) {
      await onDeleteCompanylogo();
    }

    setCroppedImage(null);
    setCardCompanyLogoUrl(cardId, null);
    setImage(null);

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }

    setInputDisabled(true);
    setTimeout(() => setInputDisabled(false), 0);
  };

  const handleClose = () => {
    setOpen(false);
    setImage(null);
    setObjectUrl(null);
    setZoom(1);
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);


  const handleSave = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'circle', `logo-${cardId}`);

      if (onCroppedImage) {
        onCroppedImage(croppedImageBlob);
      }

      const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
      setCroppedImage(croppedImageUrl);

      setCardCompanyLogoUrl(cardId, croppedImageUrl);

      setOpen(false);
    } catch (e) {
    }
  };

  return (
    <div>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '6px' }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#4f4f4f',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            Company Logo
            <Tooltip title="Ideal Dimensions: 100 px by 100 px (1:1)" arrow>
              <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
            </Tooltip>
          </Typography>
        </Box>


        <Box
          sx={{
            position: 'relative',
            width: 100,
            height: 100,
          }}
        >

          <Box
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: '#f2f2f2',
              border: '1px solid #dbdbdb',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              textAlign: 'center',
              padding: 1,
              backgroundImage: croppedImage ? `url(${croppedImage})` : 'none', // Set cropped image as background
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 1,
            }}
            component="label"
          >
            {!croppedImage && (
              <>
                <EditOutlinedIcon sx={{ fontSize: '25px', color: '#828282', marginBottom: '5px' }} />
                <Typography
                  variant="body2"
                  sx={{ fontSize: '0.7rem', fontWeight: 400, color: '#212121' }}
                >
                  <span style={{ color: 'blue' }}>Select</span> file or drag and drop here
                </Typography>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                opacity: 0,
                cursor: croppedImage ? 'default' : 'pointer',
                pointerEvents: croppedImage ? 'none' : 'auto',
              }}
            />
          </Box>

          {croppedImage && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveImage();
              }}
              sx={{
                position: 'absolute',
                top: -1,
                right: -3,
                color: '#828282',
                backgroundColor: 'rgb(255, 255, 255)',
                '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                width: 26,
                height: 26,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                zIndex: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backdropFilter: 'blur(7px)',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        }}
      >
        <Box
          sx={{
            outline: 'none',
            width: '90vw',
            maxWidth: '900px',
            height: '90vh',
            maxHeight: '700px',
          }}
        >
          {image && (
            <>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 'calc(100% - 100px)',
                  marginBottom: 2,
                }}
              >
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  onMediaLoaded={onMediaLoaded}
                  objectFit="contain"
                  centerImage={true}
                  showGrid={false}
                  minZoom={0.1}
                  maxZoom={3}
                  restrictPosition={false}
                />
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Slider
                  value={zoom}
                  onChange={(e, newValue) => setZoom(newValue)}
                  min={0.1}
                  max={3}
                  step={0.01}
                  valueLabelDisplay="auto"
                  defaultValue={1}
                  sx={{
                    marginBottom: 2,
                    width: '80%',
                    margin: '0 auto 20px auto'
                  }}
                />
                <div>
                  <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSave} variant="contained" color="primary">
                    Crop
                  </Button>
                </div>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default CompanyLogo;
